import DOMPurify from 'dompurify';
import { postMSPBoomiService, postSegmentService } from '../../services/trackingServices';
import { gtagEnvelope } from '../gtagEnvelope';
import { MSPFormData, SendAsyncPostObject, SitecoreTrialContentType } from '../../types';
import { getDataResidencyParam } from '../getDataResidencyParam';
import { getCookie } from '../cookies';
import { checkUserValidity, postMSPTrialFormDataService } from '../../services/sitecoreServices';
import { getFlowType } from '../getFlowType';
import { getEventReason } from '../getExperimentVersion';
import { facebookCAPIGTMEvent } from '../facebookCAPIGTMEvent';
import { analyticsEvent } from '../analyticsEvent';

interface MSPSubmission {
  data: SitecoreTrialContentType;
  values: SendAsyncPostObject;
  marketingOptOutSeen: boolean;
  contactPermission: boolean;
}

export const handleMSPTrialSubmission = async ({
  data,
  values,
  marketingOptOutSeen,
  contactPermission,
}: MSPSubmission) => {
  const { formType, trialLength, segExpQuery, segExpValue } = window.LPFormData;
  const flowType = getFlowType(formType);

  try {
    gtagEnvelope('event', 'cta_submit_click', {
      cta_name: `Start My Free ${trialLength}-Day Trial`,
      cta_url: `${window.location.origin}${window.location.pathname}${data.successUrl}`,
      cta_position: 'Trial Form',
    });

    await postSegmentService({
      event: 'msp_trial_form_clicked',
      properties: {
        reason: getEventReason(),
      },
    });

    const userEmail = values.email.toString().toLowerCase();
    let fullName = values.fullName.toString();
    let [firstName, ...rest] = fullName.split(' ');
    let lastName = rest.join(' ');
    // gather form data
    const formData: MSPFormData = {
      email: DOMPurify.sanitize(userEmail),
      firstName: DOMPurify.sanitize(firstName),
      lastName: DOMPurify.sanitize(lastName),
      jobTitle: values.title && DOMPurify.sanitize(values.title.toString()),
      companyName: DOMPurify.sanitize(values.company.toString()),
      companySize: values.employees && values.employees.toString(),
      phoneNumber: values.temp_phone__c && DOMPurify.sanitize(values.temp_phone__c.toString()),
      trialSku: data.sku,
      marketingOptOutShown: marketingOptOutSeen,
      contactPermission,
      clientIp: DOMPurify.sanitize(window.akCIp),
      language: DOMPurify.sanitize(getCookie('lang')) || 'en_US',
      trialLength: trialLength || 30,
      dataResidency: getDataResidencyParam(),
      flowType,
      formType,
      affiliateClickId: DOMPurify.sanitize(getCookie('clickid')),
      numberOfManagedUsers: values.usersmanaged.toString(),
      country: values.country.toString(),
      zipCode: values.zipCode.toString(),
      state: values.states.toString(),
      segExpQuery,
      segExpValue,
    };

    const bossInvalid = await checkUserValidity({
      email: userEmail,
      trialLength,
      trialSku: data.sku,
    });
    if (!bossInvalid.success) {
      return { success: false, code: bossInvalid.code };
    }

    const resp = await postMSPTrialFormDataService(formData);
    if (!resp.success) {
      return { success: false, code: resp.code };
    } else {
      gtagEnvelope('event', 'msp_success_business', {});
      facebookCAPIGTMEvent({
        contentId: 'lastpass_msp',
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phoneNumber,
      });
      await analyticsEvent(formData.email);
      formData.programName = data.inputs?.programName?.defaultValue;
      formData.programID = data.inputs?.programID?.defaultValue;
      await postMSPBoomiService({
        data: formData,
        userKey: resp.successToken,
      });
      window.location.href = `${data.successUrl}?email=${formData.email}`;
    }
    return { success: true };
  } catch (error) {
    return {
      success: false,
      code: '500',
      errorMessage: `MSP submit error: ${error.message}`,
    };
  }
};
